import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import Modal from '../components/Modal';

const PurchasesModule = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { authFetch, depos, selectedDepo, user } = useContext(AppContext);
    const { t } = useTranslation();
    const isAdmin = user?.role === 'Admin' || user?.role_name === 'Super Admin';
    const [loading, setLoading] = useState(false);
    const [localDepo, setLocalDepo] = useState(selectedDepo !== 'Global' ? selectedDepo : (user?.depo_id || 1));
    const [defaultVat, setDefaultVat] = useState(19.00);
    const [suppliers, setSuppliers] = useState([]);
    const [categories, setCategories] = useState([]);
    
    // Success State
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [savedCount, setSavedCount] = useState(0);

    // New Product Modal State
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [newProductForm, setNewProductForm] = useState({ name: '', barcode: '', sku: '', cost_price: 0, price: 0 });
    const [activeRowIdForNewProduct, setActiveRowIdForNewProduct] = useState(null);

    // Drafts Modal State
    const [isDraftsModalOpen, setIsDraftsModalOpen] = useState(false);
    const [draftPurchases, setDraftPurchases] = useState([]);
    const [selectedDrafts, setSelectedDrafts] = useState([]);

    const toggleDraftSelection = (draftId) => {
        setSelectedDrafts(prev => prev.includes(draftId) ? prev.filter(id => id !== draftId) : [...prev, draftId]);
    };

    const toggleAllDrafts = () => {
        if (selectedDrafts.length === draftPurchases.length && draftPurchases.length > 0) {
            setSelectedDrafts([]);
        } else {
            setSelectedDrafts(draftPurchases.map(d => d.id));
        }
    };

    const handleBulkApprove = async () => {
        if (!isAdmin || selectedDrafts.length === 0) return;
        if (!window.confirm(t(`Are you sure you want to approve ${selectedDrafts.length} drafts?`))) return;
        
        setLoading(true);
        let successCount = 0;
        for (const draftId of selectedDrafts) {
            try {
                const res = await authFetch(`/api/purchases/${draftId}`, {
                    method: 'PUT',
                    body: JSON.stringify({ status: 'Paid' })
                });
                if (res.ok) successCount++;
            } catch (e) {
                console.error(e);
            }
        }
        setLoading(false);
        setSelectedDrafts([]);
        fetchDraftsList();
        alert(t(`Successfully approved ${successCount} out of ${selectedDrafts.length} drafts.`));
    };

    const handleBulkReject = async () => {
        if (!isAdmin || selectedDrafts.length === 0) return;
        if (!window.confirm(t(`Are you sure you want to completely reject/delete ${selectedDrafts.length} drafts?`))) return;
        
        setLoading(true);
        let successCount = 0;
        for (const draftId of selectedDrafts) {
            try {
                const res = await authFetch(`/api/purchases/${draftId}`, {
                    method: 'DELETE'
                });
                if (res.ok) successCount++;
            } catch (e) {
                console.error(e);
            }
        }
        setLoading(false);
        setSelectedDrafts([]);
        fetchDraftsList();
        alert(t(`Successfully rejected ${successCount} out of ${selectedDrafts.length} drafts.`));
        if (id && selectedDrafts.includes(parseInt(id))) {
            navigate('/purchases/new');
        }
    };

    const searchTimeouts = useRef({});

    // Items State
    const createEmptyRow = (lastRow = null) => {
        // Inherit purchase information from the last row if available to speed up entry
        const dt = new Date();
        dt.setMinutes(dt.getMinutes() - dt.getTimezoneOffset());
        const defaultDate = dt.toISOString().slice(0, 16);
        
        return {
            id: Date.now() + Math.random(),
            product_id: '',
            name: '',
            barcode: '',
            qty: 1,
            cost_price: 0,
            vat_rate: defaultVat,
            vat_amount: 0,
            unit_cost_inc_vat: 0,
            sell_price: 0,
            line_total: 0,
            notes: '',
            suggestions: [],
            isMatched: false,
            // Purchase Info per row
            purchaseType: lastRow?.purchaseType || 'Supplier Purchase',
            supplierId: lastRow?.supplierId || '',
            supplierName: lastRow?.supplierName || '',
            invoiceNumber: lastRow?.invoiceNumber || '',
            purchaseDate: lastRow?.purchaseDate || defaultDate,
            referenceNumber: lastRow?.referenceNumber || '',
            remarks: lastRow?.remarks || '',
            category: lastRow?.category || ''
        };
    };

    const [rows, setRows] = useState([createEmptyRow()]);

    useEffect(() => {
        fetchSettings();
        fetchSuppliers();
        fetchCategories();
        if (id) {
            fetchPurchaseForEdit(id);
        }
    }, [id]);

    const fetchDraftsList = async () => {
        try {
            const res = await authFetch('/api/purchases?status=Draft');
            const data = await res.json();
            setDraftPurchases(Array.isArray(data) ? data : (data.data || []));
        } catch (e) {
            console.error("Failed to fetch drafts", e);
        }
    };

    const handleApproveDraft = async (draftId) => {
        if (!isAdmin) return;
        if (window.confirm(t('Approve this draft instantly?'))) {
            try {
                const res = await authFetch(`/api/purchases/${draftId}`, {
                    method: 'PUT',
                    body: JSON.stringify({ status: 'Paid' })
                });
                if (res.ok) {
                    alert(t('Draft approved successfully!'));
                    fetchDraftsList();
                } else {
                    const d = await res.json();
                    alert(d.message || t('Failed to approve draft'));
                }
            } catch (e) {
                console.error(e);
                alert(t('An error occurred'));
            }
        }
    };

    const handleDeleteDraft = async (draftId) => {
        if (!isAdmin) return;
        if (window.confirm(t('Are you sure you want to completely reject/delete this draft?'))) {
            try {
                const res = await authFetch(`/api/purchases/${draftId}`, {
                    method: 'DELETE'
                });
                if (res.ok) {
                    alert(t('Draft rejected successfully!'));
                    fetchDraftsList();
                    // If we were currently editing this draft, maybe redirect to new?
                    if (id && parseInt(id) === parseInt(draftId)) {
                        navigate('/purchases/new');
                    }
                } else {
                    const d = await res.json();
                    alert(d.message || t('Failed to delete draft'));
                }
            } catch (e) {
                console.error(e);
                alert(t('An error occurred'));
            }
        }
    };

    const fetchPurchaseForEdit = async (purchaseId) => {
        try {
            const res = await authFetch(`/api/purchases/${purchaseId}`);
            if (!res.ok) throw new Error("Not found");
            const data = await res.json();
            
            // Map items to rows
            if (data.items && data.items.length > 0) {
                const mappedRows = data.items.map(item => {
                    const vatRate = parseFloat(item.vat_rate || 19.00);
                    const qty = parseFloat(item.qty || 1);
                    const costHT = parseFloat(item.cost_price || 0);
                    const vatPerUnit = costHT * (vatRate / 100);
                    const lineTotal = (costHT + vatPerUnit) * qty;

                    return {
                        id: item.id || Date.now() + Math.random(),
                        product_id: item.product_id,
                        name: item.product_name,
                        barcode: item.barcode,
                        qty: qty,
                        cost_price: costHT,
                        vat_rate: vatRate,
                        vat_amount: (vatPerUnit * qty).toFixed(2),
                        unit_cost_inc_vat: (costHT + vatPerUnit).toFixed(2),
                        sell_price: item.price || 0,
                        line_total: lineTotal.toFixed(2),
                        notes: item.notes || '',
                        suggestions: [],
                        isMatched: true,
                        // Purchase Master Data
                        purchaseType: data.purchase_type,
                        supplierId: data.supplier_id || '',
                        supplierName: data.supplier_name || '',
                        invoiceNumber: data.invoice_number || '',
                        purchaseDate: (data.purchase_date || '').replace(' ', 'T'),
                        referenceNumber: data.reference_number || '',
                        remarks: data.remarks || '',
                        category: data.category || ''
                    };
                });
                setRows(mappedRows);
            } else {
                // If the draft has no items yet, just prefill the first row with the supplier/date info
                setRows([{
                    ...createEmptyRow(),
                    purchaseType: data.purchase_type,
                    supplierId: data.supplier_id || '',
                    supplierName: data.supplier_name || '',
                    invoiceNumber: data.invoice_number || '',
                    purchaseDate: (data.purchase_date || '').replace(' ', 'T'),
                    referenceNumber: data.reference_number || '',
                    remarks: data.remarks || '',
                    category: data.category || ''
                }]);
            }
            if (data.depo_id) setLocalDepo(data.depo_id);
        } catch (e) {
            console.error(e);
            alert("Could not load purchase for editing.");
        }
    };

    useEffect(() => {
        setLocalDepo(selectedDepo !== 'Global' ? selectedDepo : (user?.depo_id || 1));
    }, [selectedDepo, user]);

    const fetchSettings = async () => {
        try {
            const res = await authFetch('/api/settings');
            const data = await res.json();
            if (data.default_purchase_vat) {
                const vat = parseFloat(data.default_purchase_vat);
                setDefaultVat(vat);
                setRows(prevRows => prevRows.map(r => r.isMatched ? r : { ...r, vat_rate: vat }));
            }
        } catch (e) {
            console.error(e);
        }
    };

    const fetchSuppliers = async () => {
        try {
            const res = await authFetch('/api/suppliers');
            const data = await res.json();
            setSuppliers(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await authFetch('/api/categories');
            const data = await res.json();
            setCategories(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
        }
    };

    const addRow = () => {
        const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
        setRows([...rows, createEmptyRow(lastRow)]);
    };
    
    const removeRow = (id) => {
        if (rows.length > 1) setRows(rows.filter(r => r.id !== id));
    };

    const searchProduct = async (id, query) => {
        if (!query || query.length < 2) {
            updateRow(id, 'suggestions', []);
            return;
        }
        try {
            const res = await authFetch(`/api/products?search=${encodeURIComponent(query)}`);
            const data = await res.json();
            updateRow(id, 'suggestions', Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
        }
    };

    const selectProduct = (rowId, prod) => {
        setRows(rows.map(r => {
            if (r.id === rowId) {
                const cost = parseFloat(prod.cost_price || 0) > 0 ? parseFloat(prod.cost_price) : (parseFloat(r.cost_price) || 0);
                const sellPrice = parseFloat(prod.price || 0) > 0 ? parseFloat(prod.price) : (parseFloat(r.sell_price) || 0);
                const vatRate = parseFloat(r.vat_rate) || 19;
                const vatAmt = cost * (vatRate / 100);
                const qty = parseFloat(r.qty) || 1;
                const lineTotal = (cost + vatAmt) * qty;
                return {
                    ...r,
                    product_id: prod.id,
                    name: prod.name,
                    barcode: prod.barcode || r.barcode || '',
                    cost_price: cost,
                    unit_cost_inc_vat: (cost + vatAmt).toFixed(2),
                    sell_price: sellPrice,
                    vat_amount: (vatAmt * qty).toFixed(2),
                    line_total: lineTotal.toFixed(2),
                    isMatched: true,
                    suggestions: []
                };
            }
            return r;
        }));
    };

    const updateRow = (id, field, value) => {
        setRows(prevRows => prevRows.map(r => {
            if (r.id === id) {
                const newRow = { ...r, [field]: value };
                if (field === 'name' && !r.isMatched) {
                    if (searchTimeouts.current[id]) clearTimeout(searchTimeouts.current[id]);
                    searchTimeouts.current[id] = setTimeout(() => {
                        searchProduct(id, value);
                    }, 600); // Wait 600ms for user to finish typing
                }
                if (['qty', 'cost_price', 'vat_rate'].includes(field)) {
                    const qty = parseFloat(newRow.qty || 0);
                    const cost = parseFloat(newRow.cost_price || 0);
                    const vatRate = parseFloat(newRow.vat_rate || 0);
                    const vatPerUnit = cost * (vatRate / 100);
                    newRow.vat_amount = (vatPerUnit * qty).toFixed(2);
                    newRow.unit_cost_inc_vat = (cost + vatPerUnit).toFixed(2);
                    newRow.line_total = ((cost + vatPerUnit) * qty).toFixed(2);
                }
                if (field === 'supplierId') {
                    // Reset custom supplier name if they pick from list
                    if (value) newRow.supplierName = '';
                }
                return newRow;
            }
            return r;
        }));
    };

    const handleCreateNewProduct = async (e) => {
        e.preventDefault();
        try {
            const res = await authFetch('/api/products', {
                method: 'POST',
                body: JSON.stringify({ ...newProductForm, sku: newProductForm.sku || `SKU-${Date.now()}` })
            });
            const data = await res.json();
            if (res.ok) {
                setIsProductModalOpen(false);
                if (activeRowIdForNewProduct) {
                    const rowObj = rows.find(r => r.id === activeRowIdForNewProduct);
                    const newId = data.id || data.product_id || (data.product && data.product.id);
                    selectProduct(activeRowIdForNewProduct, data.product || { 
                        ...newProductForm, 
                        id: newId,
                        cost_price: rowObj?.cost_price || newProductForm.cost_price,
                        price: rowObj?.sell_price || newProductForm.price
                    });
                }
            } else {
                alert(data.message);
            }
        } catch (e) {
            console.error(e);
            alert("Failed to create product");
        }
    };

    const grandTotal = rows.reduce((sum, r) => sum + parseFloat(r.line_total || 0), 0);
    const totalVat = rows.reduce((sum, r) => sum + parseFloat(r.vat_amount || 0), 0);

    const handleSavePurchase = async (status = 'Paid') => {
        const nonEmptyRows = rows.filter(r => r.name && r.name.trim());
        if (nonEmptyRows.length === 0) {
            alert(t('Please add at least one item'));
            return;
        }

        setLoading(true);

        // Auto-match or auto-create any items that were typed but not explicitly selected from dropdown
        const resolvedRows = [];
        for (let i = 0; i < nonEmptyRows.length; i++) {
            const row = { ...nonEmptyRows[i] };
            if (!row.isMatched || !row.product_id) {
                const query = row.name.trim();
                let matchedProduct = null;
                try {
                    const searchRes = await authFetch(`/api/products?search=${encodeURIComponent(query)}`);
                    if (searchRes.ok) {
                        const searchData = await searchRes.json();
                        if (Array.isArray(searchData) && searchData.length > 0) {
                            matchedProduct = searchData.find(p => 
                                (p.name && p.name.trim().toLowerCase() === query.toLowerCase()) ||
                                (p.barcode && p.barcode.trim().toLowerCase() === query.toLowerCase())
                            );
                        }
                    }
                } catch (e) {
                    console.error("Search error on save", e);
                }

                if (matchedProduct) {
                    row.product_id = matchedProduct.id;
                    row.isMatched = true;
                    if (!row.barcode && matchedProduct.barcode) row.barcode = matchedProduct.barcode;
                } else {
                    // Auto-create product in catalog so user never loses their typed rows!
                    try {
                        const supplierFromList = suppliers.find(s => String(s.id) === String(row.supplierId));
                        const actualSupplierName = row.supplierName || (supplierFromList ? supplierFromList.name : '');
                        const createRes = await authFetch('/api/products', {
                            method: 'POST',
                            body: JSON.stringify({
                                name: query,
                                sku: row.referenceNumber || `SKU-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                                category: row.category || 'General',
                                origin: actualSupplierName || 'Local',
                                preferred_supplier: actualSupplierName,
                                cost_price: parseFloat(row.cost_price || 0),
                                price: parseFloat(row.sell_price || 0),
                                vat_rate: (parseFloat(row.vat_rate || 19) / 100),
                                barcode: row.barcode || query
                            })
                        });
                        const createData = await createRes.json();
                        const newId = createData.id || createData.product_id || (createData.product && createData.product.id);
                        if (newId) {
                            row.product_id = newId;
                            row.isMatched = true;
                        }
                    } catch (e) {
                        console.error("Auto-create error on save", e);
                    }
                }
            }

            if (row.isMatched && row.product_id) {
                resolvedRows.push(row);
            } else {
                setLoading(false);
                alert(t(`Item "${row.name}" could not be linked or created. Please verify details.`));
                return;
            }
        }

        // Validate supplier requirement
        const invalidSupplier = resolvedRows.find(r => r.purchaseType === 'Supplier Purchase' && !r.supplierId && !r.supplierName);
        if (invalidSupplier) {
            setLoading(false);
            alert(t('Supplier is required for Supplier Purchases on all valid rows.'));
            return;
        }

        // Group rows by unique purchase signatures
        const groups = {};
        resolvedRows.forEach(row => {
            const supplierFromList = suppliers.find(s => String(s.id) === String(row.supplierId));
            const actualSupplierName = row.supplierName || (supplierFromList ? supplierFromList.name : 'N/A');
            
            const key = `${row.supplierId}_${actualSupplierName}_${row.invoiceNumber}_${row.purchaseType}_${row.purchaseDate}`;
            if (!groups[key]) {
                groups[key] = {
                    depo_id: localDepo,
                    purchase_type: row.purchaseType,
                    supplier_id: row.supplierId,
                    supplier_name: actualSupplierName,
                    supplier: actualSupplierName,
                    invoice_number: row.invoiceNumber,
                    purchase_date: row.purchaseDate.replace('T', ' ') + ':00',
                    reference_number: row.referenceNumber,
                    remarks: row.remarks,
                    category: row.category,
                    grand_total: 0,
                    total_vat: 0,
                    items: []
                };
            }
            groups[key].items.push({
                product_id: row.product_id,
                qty: row.qty,
                cost_price: row.cost_price,
                sell_price: row.sell_price,
                vat_rate: row.vat_rate,
                vat_amount: row.vat_amount,
                line_total: row.line_total,
                notes: row.notes
            });
            groups[key].grand_total += parseFloat(row.line_total || 0);
            groups[key].total_vat += parseFloat(row.vat_amount || 0);
        });

        setLoading(true);
        try {
            if (id) {
                // EDIT MODE
                const payload = Object.values(groups)[0]; // Should only be one for edit
                const finalStatus = (payload.purchase_type === 'Supplier Purchase' && status === 'Paid') ? 'Credit' : status;
                const res = await authFetch(`/api/purchases/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ ...payload, status: finalStatus })
                });
                if (res.ok) {
                    setSavedCount(1);
                    setSaveSuccess(true);
                    setTimeout(() => {
                        navigate('/purchases/today');
                    }, 2000);
                } else {
                    const err = await res.json();
                    alert(err.message || 'Error updating purchase');
                }
            } else {
                // NEW MODE: Iterate through grouped purchases and create multiple invoices
                let count = 0;
                for (let key in groups) {
                    const payload = groups[key];
                    const finalStatus = (payload.purchase_type === 'Supplier Purchase' && status === 'Paid') ? 'Credit' : status;
                    const res = await authFetch('/api/purchases', {
                        method: 'POST',
                        body: JSON.stringify({ ...payload, status: finalStatus })
                    });
                    if (res.ok) {
                        count++;
                    } else {
                        const err = await res.json();
                        alert(`Error saving one invoice: ${err.message}`);
                    }
                }
                
                if (count === 0) {
                    return;
                }
                
                setSavedCount(count);
                setSaveSuccess(true);
                
                // Reset form
                setTimeout(() => {
                    setSaveSuccess(false);
                    setRows([createEmptyRow()]);
                }, 3000);
            }
        } catch (e) {
            alert(t('Network Error'));
        } finally {
            setLoading(false);
        }
    };

    const startNewPurchase = () => {
        setRows([createEmptyRow()]);
        setSaveSuccess(false);
        setSavedCount(0);
    };

    if (saveSuccess) {
        return (
            <div className="p-8 bg-white rounded-lg shadow-md max-w-lg mx-auto mt-10 text-center">
                <div className="text-green-500 text-6xl mb-4">✅</div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('Purchases saved successfully.')}</h2>
                <p className="text-gray-600 mb-8">{savedCount} {t('distinct purchase invoice(s) created.')}</p>
                <button 
                    onClick={startNewPurchase}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
                >
                    {t('Start New Entry')}
                </button>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-8">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <h1 className="text-2xl font-bold text-gray-800">
                        {id ? t('Edit Purchase') : t('New Purchase')}
                    </h1>
                    <button 
                        onClick={() => { fetchDraftsList(); setIsDraftsModalOpen(true); }}
                        className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 px-3 py-1.5 rounded-lg text-sm font-bold flex items-center transition"
                    >
                        📋 {t('Review Pending Drafts')}
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">{t('Destination Branch')}:</label>
                    <select 
                        value={localDepo} 
                        onChange={(e) => setLocalDepo(e.target.value)}
                        className="p-2 border border-gray-300 rounded-md shadow-sm bg-white font-semibold text-indigo-700"
                    >
                        {depos.map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-left text-gray-500 whitespace-nowrap">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-100 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 sticky left-0 bg-gray-100 z-10 w-48 shadow-[1px_0_0_#e5e7eb]">{t('Item Search')}</th>
                                <th className="px-4 py-3 w-20">{t('Qty')}</th>
                                <th className="px-4 py-3 w-24">{t('Cost Price')}</th>
                                <th className="px-4 py-3 w-20">{t('VAT %')}</th>
                                <th className="px-4 py-3 w-24">{t('Cost+VAT')}</th>
                                <th className="px-4 py-3 w-24">{t('Sell Price')}</th>
                                <th className="px-4 py-3 w-24">{t('VAT Amt')}</th>
                                <th className="px-4 py-3 w-24">{t('Total')}</th>
                                <th className="px-4 py-3 w-32">{t('Type')}</th>
                                <th className="px-4 py-3 w-40">{t('Supplier')}</th>
                                <th className="px-4 py-3 w-32">{t('Invoice #')}</th>
                                <th className="px-4 py-3 w-40">{t('Date')}</th>
                                <th className="px-4 py-3 w-32">{t('Category')}</th>
                                <th className="px-4 py-3 w-32">{t('Ref #')}</th>
                                <th className="px-4 py-3 w-32">{t('Notes/Remarks')}</th>
                                <th className="px-4 py-3 w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const isDropdownOpen = (!row.isMatched && row.name.length >= 2 && row.suggestions?.length === 0) || (row.suggestions?.length > 0);
                                return (
                                <tr key={row.id} className="border-b hover:bg-gray-50">
                                    <td className={`px-2 py-2 relative sticky left-0 bg-white shadow-[1px_0_0_#e5e7eb] ${isDropdownOpen ? 'z-50' : 'z-10'}`}>
                                        <input
                                            type="text"
                                            value={row.name}
                                            onChange={(e) => updateRow(row.id, 'name', e.target.value)}
                                            placeholder={t('Search...')}
                                            className={`w-full p-2 border rounded-md text-sm focus:ring-indigo-500 ${!row.isMatched && row.name.length > 0 ? 'border-yellow-400' : 'border-gray-300'}`}
                                        />
                                        {!row.isMatched && row.name.length >= 2 && row.suggestions?.length === 0 && (
                                            <div className="absolute z-20 w-56 bg-white border border-gray-200 p-2.5 mt-1 rounded-lg shadow-lg text-xs">
                                                <span className="text-amber-600 font-semibold block">✨ {t('New Item (Auto-created on save)')}</span>
                                                <button 
                                                    type="button"
                                                    onClick={() => { 
                                                        setIsProductModalOpen(true); 
                                                        setActiveRowIdForNewProduct(row.id); 
                                                        setNewProductForm(f => ({ 
                                                            ...f, 
                                                            name: row.name,
                                                            cost_price: row.cost_price || '',
                                                            price: row.sell_price || '',
                                                            category: row.category || ''
                                                        })); 
                                                    }}
                                                    className="inline-block mt-1.5 text-indigo-600 font-medium hover:underline"
                                                >
                                                    {t('+ Customize in Catalog')}
                                                </button>
                                            </div>
                                        )}
                                        {row.suggestions?.length > 0 && (
                                            <ul className="absolute z-20 w-full bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-48 overflow-y-auto">
                                                {row.suggestions.map(s => (
                                                    <li 
                                                        key={s.id} 
                                                        className="p-2 hover:bg-indigo-50 cursor-pointer text-sm"
                                                        onClick={() => selectProduct(row.id, s)}
                                                    >
                                                        {s.name} <span className="text-gray-400 text-xs">({s.barcode || s.sku})</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </td>
                                    <td className="px-2 py-2">
                                        <input type="number" min="0.001" step="any" value={row.qty} onChange={(e) => updateRow(row.id, 'qty', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input type="number" min="0" step="any" value={row.cost_price} onChange={(e) => updateRow(row.id, 'cost_price', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input 
                                            type="number" min="0" step="any" value={row.vat_rate} 
                                            onChange={(e) => updateRow(row.id, 'vat_rate', e.target.value)} 
                                            className="w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100" 
                                            disabled={user?.role !== 'Admin' && user?.role_name !== 'Super Admin'}
                                        />
                                    </td>
                                    <td className="px-2 py-2 text-gray-700 font-medium text-center">
                                        {row.unit_cost_inc_vat || '0.00'}
                                    </td>
                                    <td className="px-2 py-2">
                                        <input type="number" min="0" step="any" value={row.sell_price} onChange={(e) => updateRow(row.id, 'sell_price', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md" />
                                    </td>
                                    <td className="px-2 py-2 text-gray-700 font-medium text-center">
                                        {row.vat_amount}
                                    </td>
                                    <td className="px-2 py-2 text-gray-700 font-bold text-center">
                                        {row.line_total}
                                    </td>
                                    <td className="px-2 py-2">
                                        <select 
                                            value={row.purchaseType}
                                            onChange={(e) => updateRow(row.id, 'purchaseType', e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                        >
                                            <option value="Supplier Purchase">Supplier</option>
                                            <option value="Local Purchase">Local</option>
                                        </select>
                                    </td>
                                    <td className="px-2 py-2">
                                        <div className="flex flex-col gap-1 w-40">
                                            <select 
                                                value={row.supplierId}
                                                onChange={(e) => updateRow(row.id, 'supplierId', e.target.value)}
                                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                            >
                                                <option value="">{t('Select...')}</option>
                                                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                            </select>
                                            {!row.supplierId && (
                                                <input 
                                                    type="text" 
                                                    placeholder={t('Or new...')} 
                                                    value={row.supplierName}
                                                    onChange={(e) => updateRow(row.id, 'supplierName', e.target.value)}
                                                    className="w-full p-2 border border-gray-300 rounded-md text-sm bg-gray-50"
                                                />
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-2 py-2">
                                        <input 
                                            type="text" 
                                            value={row.invoiceNumber}
                                            onChange={(e) => updateRow(row.id, 'invoiceNumber', e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                            placeholder="INV-..."
                                        />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input 
                                            type="datetime-local" 
                                            value={row.purchaseDate}
                                            onChange={(e) => updateRow(row.id, 'purchaseDate', e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md text-xs"
                                        />
                                    </td>
                                    <td className="px-2 py-2">
                                        <select
                                            value={row.category}
                                            onChange={(e) => {
                                                if (e.target.value === '__ADD_NEW__') {
                                                    const newCat = window.prompt(t('Enter new category name (e.g. Rayon 1, Food):'));
                                                    if (newCat && newCat.trim()) {
                                                        const catName = newCat.trim();
                                                        authFetch('/api/categories', { 
                                                            method: 'POST', 
                                                            body: JSON.stringify({ name: catName }) 
                                                        })
                                                            .then(res => res.json())
                                                            .then((data) => {
                                                                const finalName = data.name || catName;
                                                                setCategories(prev => [...prev.filter(c => c.name.toLowerCase() !== finalName.toLowerCase()), { id: data.id || Date.now(), name: finalName }]);
                                                                updateRow(row.id, 'category', finalName);
                                                            })
                                                            .catch(console.error);
                                                    }
                                                } else {
                                                    updateRow(row.id, 'category', e.target.value);
                                                }
                                            }}
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                        >
                                            <option value="">-- {t('Category')} --</option>
                                            {categories.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                                            {row.category && !categories.some(c => c.name.toLowerCase() === row.category.toLowerCase()) && (
                                                <option value={row.category}>{row.category}</option>
                                            )}
                                            <option value="__ADD_NEW__">+ {t('Add New (e.g. Rayon 1)')}</option>
                                        </select>
                                    </td>
                                    <td className="px-2 py-2">
                                        <input 
                                            type="text" 
                                            value={row.referenceNumber}
                                            onChange={(e) => updateRow(row.id, 'referenceNumber', e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                        />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input 
                                            type="text" 
                                            value={row.notes} 
                                            onChange={(e) => updateRow(row.id, 'notes', e.target.value)} 
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm" 
                                            placeholder={t('Notes/Remarks')} 
                                        />
                                    </td>
                                    <td className="px-2 py-2 text-center">
                                        <button onClick={() => removeRow(row.id)} className="text-red-500 hover:text-red-700 font-bold text-xl">&times;</button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                
                <div className="p-4 bg-gray-50 border-t flex justify-between items-center">
                    <button onClick={addRow} className="text-indigo-600 font-medium hover:text-indigo-800 flex items-center bg-white px-4 py-2 border rounded shadow-sm">
                        <span className="text-xl mr-2">+</span> {t('Add Row')}
                    </button>
                    <div className="text-right">
                        <div className="text-gray-600 text-sm">{t('Total VAT')}: <span className="font-semibold text-gray-800">{totalVat.toFixed(2)}</span></div>
                        <div className="text-xl text-gray-700 font-bold mt-1">{t('Total Pending Cost')}: <span className="text-indigo-700">{grandTotal.toFixed(2)}</span></div>
                    </div>
                </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
                <button 
                    onClick={() => handleSavePurchase('Draft')} 
                    disabled={loading}
                    className="bg-gray-200 text-gray-800 px-8 py-3 rounded-lg shadow font-semibold hover:bg-gray-300 transition disabled:opacity-50"
                >
                    {loading ? t('Saving...') : t('Save as Draft')}
                </button>
                <button 
                    onClick={() => handleSavePurchase('Paid')} 
                    disabled={loading}
                    className="bg-indigo-600 text-white px-8 py-3 rounded-lg shadow font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
                >
                    {loading ? t('Saving...') : t('Save & Complete')}
                </button>
            </div>

            {/* Create New Product Modal */}
            <Modal isOpen={isProductModalOpen} onClose={() => setIsProductModalOpen(false)} title={t('Create New Product')}>
                <form onSubmit={handleCreateNewProduct} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('Name')} *</label>
                        <input required type="text" value={newProductForm.name} onChange={e => setNewProductForm({...newProductForm, name: e.target.value})} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('Barcode')}</label>
                            <input type="text" value={newProductForm.barcode} onChange={e => setNewProductForm({...newProductForm, barcode: e.target.value})} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('SKU')}</label>
                            <input type="text" value={newProductForm.sku} onChange={e => setNewProductForm({...newProductForm, sku: e.target.value})} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" placeholder="Auto-generated if empty" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('Cost Price')}</label>
                            <input type="number" step="any" value={newProductForm.cost_price} onChange={e => setNewProductForm({...newProductForm, cost_price: e.target.value})} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('Retail Price')}</label>
                            <input type="number" step="any" value={newProductForm.price} onChange={e => setNewProductForm({...newProductForm, price: e.target.value})} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>
                    <div className="flex justify-end pt-4 border-t">
                        <button type="button" onClick={() => setIsProductModalOpen(false)} className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">{t('Cancel')}</button>
                        <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700">{t('Create Product')}</button>
                    </div>
                </form>
            </Modal>

            {/* Pending Drafts Modal */}
            <Modal isOpen={isDraftsModalOpen} onClose={() => setIsDraftsModalOpen(false)} title={t('Pending Drafts')} size="5xl">
                <div className="overflow-x-auto max-h-[70vh]">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-100 uppercase text-xs text-gray-600">
                            <tr>
                                <th className="p-3 w-10 text-center">
                                    <input type="checkbox" checked={selectedDrafts.length === draftPurchases.length && draftPurchases.length > 0} onChange={toggleAllDrafts} className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 w-4 h-4 cursor-pointer" />
                                </th>
                                <th className="p-3">ID</th>
                                <th className="p-3">{t('Supplier')}</th>
                                <th className="p-3">{t('Date')}</th>
                                <th className="p-3 text-right">{t('Total')}</th>
                                <th className="p-3 text-center">{t('Action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {draftPurchases.length === 0 && (
                                <tr><td colSpan="6" className="p-4 text-center text-gray-500">{t('No pending drafts found.')}</td></tr>
                            )}
                            {Object.entries(
                                draftPurchases.reduce((acc, draft) => {
                                    const datePart = (draft.purchase_date || draft.created_at || '').split(' ')[0];
                                    if (!acc[datePart]) acc[datePart] = [];
                                    acc[datePart].push(draft);
                                    return acc;
                                }, {})
                            )
                            .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                            .map(([date, drafts]) => (
                                <React.Fragment key={date}>
                                    <tr className="bg-indigo-50/80 border-y border-indigo-100">
                                        <td colSpan="6" className="p-3 font-black text-indigo-900 text-xs tracking-widest uppercase sticky left-0">
                                            📅 {new Date(date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                        </td>
                                    </tr>
                                    {drafts.map(draft => (
                                        <tr key={draft.id} className="border-b hover:bg-gray-50/50">
                                            <td className="p-3 text-center">
                                                <input type="checkbox" checked={selectedDrafts.includes(draft.id)} onChange={() => toggleDraftSelection(draft.id)} className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 w-4 h-4 cursor-pointer" />
                                            </td>
                                            <td className="p-3 font-mono text-gray-500">#{draft.id}</td>
                                            <td className="p-3 font-medium">{draft.supplier_name || 'N/A'}</td>
                                            <td className="p-3 text-gray-500 text-xs">{draft.purchase_date || draft.created_at}</td>
                                            <td className="p-3 text-right text-red-600 font-bold">{parseFloat(draft.grand_total || draft.total_cost || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                                            <td className="p-3">
                                                <div className="flex items-center justify-center gap-2">
                                                    {isAdmin && (
                                                        <button 
                                                            onClick={() => handleApproveDraft(draft.id)}
                                                            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded flex items-center gap-1 text-xs font-bold transition-colors"
                                                            title="Approve Instantly"
                                                        >
                                                            ✅ {t('Approve')}
                                                        </button>
                                                    )}
                                                    <button 
                                                        onClick={() => {
                                                            setIsDraftsModalOpen(false);
                                                            navigate(`/purchases/edit/${draft.id}`);
                                                        }}
                                                        className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded flex items-center gap-1 text-xs font-bold transition-colors"
                                                        title="Review Details"
                                                    >
                                                        ✏️ {t('Review')}
                                                    </button>
                                                    {isAdmin && (
                                                        <button 
                                                            onClick={() => handleDeleteDraft(draft.id)}
                                                            className="bg-red-50 hover:bg-red-100 text-red-700 px-3 py-1.5 rounded flex items-center gap-1 text-xs font-bold transition-colors"
                                                            title="Reject Draft"
                                                        >
                                                            🗑️ {t('Reject')}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex justify-between mt-4">
                    <div className="flex gap-2">
                        {isAdmin && selectedDrafts.length > 0 && (
                            <>
                                <button onClick={handleBulkApprove} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded text-sm font-bold transition-colors shadow-sm">
                                    ✅ {t('Approve Selected')} ({selectedDrafts.length})
                                </button>
                                <button onClick={handleBulkReject} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-bold transition-colors shadow-sm">
                                    🗑️ {t('Reject Selected')} ({selectedDrafts.length})
                                </button>
                            </>
                        )}
                    </div>
                    <button onClick={() => setIsDraftsModalOpen(false)} className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded text-sm font-bold transition-colors">{t('Close')}</button>
                </div>
            </Modal>

        </div>
    );
};

export default PurchasesModule;
